import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CarRepairIcon from '@mui/icons-material/CarRepair';
import FileDownload from '@mui/icons-material/FileDownload';
import {
  AppBar,
  Button,
  CircularProgress,
  Grid,
  Link,
  Paper,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { ApiError, TaxonService } from '../client';
import { useStore } from '../store';
import { encodeURISciName, isDebug } from '../utils';
import { BloomRow } from './BloomRow';

const Tp = Typography;

export function MultiSearch() {
  const store = useStore((state) => state);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [state, setState] = useImmer({
    hoveredBloomtime: undefined as string | undefined,
    csvError: undefined as string | undefined,
  });

  const lines = (store.persistent.multiSearchInput || '')
    .split('\n')
    .map((term) => term.trim());

  const qs = useQueries({
    queries: lines
      .filter((term) => !!term)
      .map((name_sci) => ({
        queryKey: ['taxonDetailApiV1TaxonDetailGet', name_sci],
        queryFn: () =>
          TaxonService.taxonDetailApiV1TaxonDetailGet({
            q: name_sci,
          }).then((tx) => {
            const searchedFor = name_sci;
            const foundExact =
              normalizedCompare(searchedFor, tx.name_sci || '') ||
              (tx.name_comm || []).some((c) =>
                normalizedCompare(searchedFor, c)
              );
            return { ...tx, foundExact };
          }),
      })),
    combine: (results) => {
      if (results.every((r) => r.status === 'success'))
        return {
          status: 'success' as 'success',
          data: results.map((r) => r.data),
          raw: results,
          showNoteAboutInexact: !results.every((r) => r.data?.foundExact),
        };
      if (results.some((r) => r.status === 'error'))
        return {
          status: 'error' as 'error',
          raw: results,
        };
      return {
        status: 'pending' as 'pending',
        raw: results,
      };
    },
  });

  const downloadCSV = async () => {
    if (qs.data === undefined) {
      setState((draft) => {
        draft.csvError = 'query data missing';
      });
      return;
    }
    setState((draft) => {
      draft.csvError = undefined;
    });
    const nameScis1 = qs.data.map((d) => d?.name_sci);
    const nameScis: string[] = nameScis1.filter(
      (x): x is string => x !== undefined
    );
    if (nameScis.length !== nameScis1.length)
      console.warn('download csv: found empty names', qs.data);
    console.log('csv download of', nameScis);

    try {
      const response = await fetch('/api/v1/taxon/csv', {
        method: 'POST',
        body: JSON.stringify(nameScis),
        headers: { 'content-type': 'application/json' },
      });
      console.log(response);
      if (!response.ok) {
        console.error('csv fetch failed', response);
        throw new Error(response.statusText);
      }
      const blob = await response.blob();
      // console.log('csv', await response.text())
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', 'plants.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      console.debug('csv download complete');
    } catch (err) {
      console.error('csv download failed', err);
      let errMsg = 'csv download failed';
      if (err instanceof Error) {
        errMsg = err.message;
      }
      setState((draft) => {
        draft.csvError = errMsg;
      });
    }
  };

  return (
    <Grid container direction="column">
      <AppBar position="sticky" sx={{ zIndex: 2000 }}>
        <Toolbar>
          <Grid container direction="row" alignItems="center">
            <Link
              to=".."
              underline="none"
              color="inherit"
              component={RouterLink}
              onClick={(e) => {
                e.preventDefault();
                navigate(-1);
              }}
            >
              <ArrowBackIosNewIcon />
            </Link>
            <Grid container item flex={1} justifyContent="center">
              <Tp>Multiple Search</Tp>
            </Grid>
          </Grid>
          {isDebug() && (
            <Button
              onClick={(e) => {
                e.preventDefault();
                // eslint-disable-next-line no-console
                console.log('clearing cache');
                queryClient.clear();
              }}
            >
              <CarRepairIcon />
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <Grid
        container
        item
        direction="row"
        justifyContent="center"
        sx={{ paddingTop: 2, paddingX: 2 }}
      >
        <Grid container item direction="column" xs={12} sm={8} md={6}>
          <Tp>Search for multiple plants:</Tp>
          <Grid container item direction="row">
            <Grid item xs={6}>
              <TextField
                id="outlined-basic"
                variant="outlined"
                multiline
                autoFocus
                sx={{
                  marginY: 2,
                  width: '100%',
                  paddingRight: 1,
                }}
                spellCheck={false}
                value={store.persistent.multiSearchInput}
                onChange={
                  (e) => store.setMultiSearchInput(e.target.value)
                  // setState((draft) => {
                  //   draft.text = e.target.value;
                  // })
                }
              />
            </Grid>
            <Grid
              container
              item
              xs={6}
              sx={{ paddingTop: 4 }}
              direction="column"
            >
              {qs.raw.map((x, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Grid item key={i}>
                  {x.isPending && <CircularProgress />}
                  {x.error && (
                    <Paper sx={{ marginBottom: 2 }}>
                      <Grid container sx={{ color: 'red' }}>
                        <Grid item sx={{ mx: 1 }}>
                          {(x.error as ApiError).status}
                        </Grid>
                        <Grid item sx={{ mx: 1 }}>
                          {(x.error as ApiError).name}
                        </Grid>
                        <Grid item sx={{ mx: 1 }}>
                          {(x.error as ApiError).message}
                        </Grid>
                        <Grid item sx={{ mx: 1 }}>
                          {(x.error as ApiError).url}
                        </Grid>
                      </Grid>
                    </Paper>
                  )}
                  {x.isSuccess && (
                    <Link
                      to={`/taxon/${encodeURISciName(x.data.name_sci || '')}`}
                      component={RouterLink}
                    >
                      <Tp
                        sx={{
                          color:
                            x.data.bloomtime?.includes(
                              state.hoveredBloomtime || ''
                            ) ||
                            ((x.data.bloomtime || []).length === 0 &&
                              state.hoveredBloomtime === 'unknown')
                              ? 'orange'
                              : undefined,
                        }}
                      >
                        {x.data.name_sci}
                        {x.data.foundExact ? null : <> *</>}
                      </Tp>
                    </Link>
                  )}
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Tp>Bloom times</Tp>
          {qs.status === 'success' && (
            <>
              <BloomRow
                variant="count"
                onHover={(x) =>
                  setState((draft) => {
                    draft.hoveredBloomtime = x;
                  })
                }
                bloomtime={qs.data.map((d) => d?.bloomtime || [])}
              />
              {qs.data.length > 0 && (
                <Button onClick={downloadCSV}>
                  <FileDownload /> CSV
                </Button>
              )}
              {state.csvError !== undefined && (
                <Tp variant="caption" color="red">
                  csv error: {state.csvError}
                </Tp>
              )}
            </>
          )}
          {qs.status === 'pending' && '[loading]'}
          {qs.status === 'error' && '[error]'}
          {qs.showNoteAboutInexact && <Tp variant="caption">*inexact match</Tp>}
        </Grid>
      </Grid>
      {/* <BasketDrawer /> */}
    </Grid>
  );
}

function normalizedCompare(s1: string, s2: string) {
  return normalize(s1) === normalize(s2);
}

function normalize(s: string): string {
  return s.replace(/\s+/g, ' ').replace(/-|'/g, '').toLowerCase();
}
